import {useCallback, useEffect, useState} from 'react';
import Markdown from 'react-markdown';
import DOMPurify from 'dompurify';
import Button from '@/components/Button';
import styles from './index.module.scss';
import {replaceChainId} from './utils';
import {BackendFlexBuilderPromptTemplateForFlexBuilderUse} from '../../../constant';
import {copyText} from '../utils';

interface IProps {
    markdownText: string;
    scene: 'preview' | 'edit';
    chainId?: string;
    apiDocContainerDOM?: HTMLElement | null;
}

export const ApiDoc = (props: IProps) => {
    const {markdownText, scene, chainId, apiDocContainerDOM} = props;
    const [scrollContainerHeight, setScrollContainerHeight] = useState<number | string>(0);

    // 复制 markdown
    const handleCopyMarkdown = useCallback(() => {
        copyText(markdownText);
    }, [markdownText]);

    // 复制 chain id
    const handleCopyChainId = useCallback(() => {
        copyText(chainId ?? '');
    }, [chainId]);

    // 复制 prompt 模板
    const handleCopyPromptTemplate = useCallback(() => {
        const placeholder = '${提示词}';
        copyText(
            `${placeholder}。${BackendFlexBuilderPromptTemplateForFlexBuilderUse}${markdownText}`,
            `已复制，使用前请记得替换占位符(${placeholder})的内容噢！`
        );
    }, [markdownText]);

    // 监视父容器的高度变化，动态设置滚动容器的高度
    useEffect(() => {
        if (!apiDocContainerDOM) {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const {height} = entry.contentRect ?? {};
                if (height) {
                    // 60 是目前的各种边距相加🈯之和，后续按需调整
                    const containerHeight = height - 60;
                    if (containerHeight >= 0) {
                        setScrollContainerHeight(containerHeight);
                    }
                    else {
                        setScrollContainerHeight('initial');
                    }
                }
            }
        });
        observer.observe(apiDocContainerDOM);
        return () => {
            observer.unobserve(apiDocContainerDOM);
        };
    }, [apiDocContainerDOM]);

    return (
        <div className={styles.container}>
            <div style={{textAlign: scene === 'preview' ? 'right' : 'left'}}>
                {
                    markdownText && <>
                        <Button
                            type={'important'}
                            onClick={handleCopyPromptTemplate}
                        >
                            复制 prompt 模板
                        </Button>
                        <Button
                            type={'important'}
                            onClick={handleCopyMarkdown}
                        >
                            复制 markdown
                        </Button>
                        <Button
                            type={'important'}
                            onClick={handleCopyChainId}
                        >
                            复制 chain id
                        </Button>
                    </>
                }
            </div>
            <div
                className={styles['scroll-container']}
                style={{
                    height: typeof scrollContainerHeight === 'number'
                        ? `${scrollContainerHeight}px`
                        : scrollContainerHeight,
                }}
            >
                {
                    markdownText
                        ? <Markdown>{DOMPurify.sanitize(replaceChainId(markdownText, chainId))}</Markdown>
                        : <p style={{textAlign: 'center'}}>空文档</p>
                }
            </div>
        </div>
    );
};
